import { useNavigate } from 'react-router-dom';

export default function STEMLab() {
  const navigate = useNavigate();

  const goToConsultation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    sessionStorage.setItem('scrollToSection', 'contact');
    navigate('/');
  };
  const features = [
    {
      icon: 'ri-book-2-line',
      title: 'Curriculum-Aligned Kits',
      description: 'Grade-wise learning modules mapped to CBSE, ICSE, and State Board curricula'
    },
    {
      icon: 'ri-microscope-line',
      title: 'Premium Equipment',
      description: 'High-quality lab instruments, tools, and safety equipment'
    },
    {
      icon: 'ri-computer-line',
      title: 'Digital Learning Platform',
      description: 'LMS with interactive lessons, videos, and assessments'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Assessment Tools',
      description: 'Automated evaluation system with detailed progress reports'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Teacher Training',
      description: '40+ hours comprehensive training and ongoing support'
    },
    {
      icon: 'ri-parent-line',
      title: 'Parent Portal',
      description: 'Real-time updates on student progress and achievements'
    }
  ];

  const labComponents = [
    'Physics Lab Equipment',
    'Chemistry Lab Setup',
    'Biology Lab Tools',
    'Mathematics Manipulatives',
    'Electronics & Circuits',
    'Renewable Energy Kits',
    '3D Printing Station',
    'Design Thinking Tools'
  ];

  return (
    <section id="stem-lab" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Content */}
          <div>
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
              Complete Infrastructure
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
              STEM Lab Setup
            </h2>
            <p className="text-base text-gray-600 mb-7 leading-relaxed">
              Transform your classroom into a state-of-the-art STEM laboratory with hands-on learning experiences that bring textbook concepts to life.
            </p>

            <div className="space-y-3 mb-7">
              {[
                'Complete lab infrastructure setup',
                'Curriculum-aligned experiments for grades 6-12',
                'Digital resources and learning management system',
                'Teacher training and ongoing support',
                'Safety equipment and compliance certification'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0"></i>
                  <span className="text-gray-700 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl p-5 text-white mb-5">
              <div className="text-xs mb-1">Investment Range</div>
              <div className="text-3xl font-bold mb-1">₹8-15 Lakhs</div>
              <div className="text-xs text-blue-100">Customizable based on school size and requirements</div>
            </div>

            <a
              href="/#contact"
              onClick={goToConsultation}
              className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Request Detailed Proposal
            </a>
          </div>

          {/* Image */}
          <div className="order-first lg:order-last">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20STEM%20science%20laboratory%20classroom%20with%20students%20conducting%20experiments%2C%20colorful%20equipment%20on%20tables%2C%20microscopes%20and%20lab%20tools%2C%20bright%20educational%20environment%20with%20natural%20lighting%2C%20contemporary%20school%20setting&width=800&height=600&seq=stem-lab-detail-001&orientation=landscape"
                alt="STEM Lab"
                className="w-full h-[500px] object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Lab Components */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-7">
            What&apos;s Included in Your STEM Lab
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {labComponents.map((component, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1"
              >
                <i className="ri-checkbox-circle-fill text-4xl text-green-500 mb-3"></i>
                <p className="font-semibold text-gray-900">{component}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
